import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  Button,
  Input,
  InputGroup,
  InputLeftElement,
  Box,
  Text,
  VStack,
  HStack,
  Badge,
  Tag,
  Divider,
  Alert,
  AlertIcon,
  FormControl,
  FormLabel,
  FormErrorMessage,
} from '@chakra-ui/react';
import { SearchIcon, AddIcon } from '@chakra-ui/icons';
import { EquipmentSupplierType } from '../../contexts/types';
import { getMultilingualDisplayName } from '../../utils/i18nDisplay';

import { InputLimits } from '../../contexts/constants';
interface EquipmentSupplierSelectModalProps {
  /** モーダル表示フラグ */
  isOpen: boolean;
  /** モーダルクローズ関数 */
  onClose: () => void;
  /** サプライヤーデータ */
  equipmentSuppliers: EquipmentSupplierType[];
  /** 選択時のコールバック関数 */
  onSelect: (supplier: EquipmentSupplierType) => void;
  /** 仮登録コールバック（未指定時は自由入力フォーム非表示） */
  onRegisterCandidate?: (name: string, nameEn: string) => Promise<EquipmentSupplierType | null>;
}

/**
 * サプライヤー選択モーダル
 * 検索機能付きのサプライヤー選択モーダル（仮登録機能付き）
 */
const EquipmentSupplierSelectModal: React.FC<EquipmentSupplierSelectModalProps> = ({
  isOpen,
  onClose,
  equipmentSuppliers,
  onSelect,
  onRegisterCandidate,
}) => {
  const { t } = useTranslation();
  /** 検索キーワード */
  const [searchKeyword, setSearchKeyword] = useState<string>('');
  /** 自由入力モード */
  const [isAddMode, setIsAddMode] = useState<boolean>(false);
  /** 新規サプライヤー名 */
  const [newSupplierName, setNewSupplierName] = useState<string>('');
  /** 新規サプライヤー名（英語） */
  const [newSupplierNameEn, setNewSupplierNameEn] = useState<string>('');
  /** 登録中フラグ */
  const [isRegistering, setIsRegistering] = useState<boolean>(false);
  /** バリデーションエラー */
  const [addErrors, setAddErrors] = useState<{ name?: string; nameEn?: string }>({});

  /** モーダルが開かれた時にリセット */
  useEffect(() => {
    if (isOpen) {
      setSearchKeyword('');
      setIsAddMode(false);
      setNewSupplierName('');
      setNewSupplierNameEn('');
      setAddErrors({});
    }
  }, [isOpen]);

  /** フィルタリングされたサプライヤーリスト（表示言語基準でソート） */
  const filteredSuppliers = useMemo(() => {
    let suppliers = equipmentSuppliers;

    // 検索キーワードでフィルタリング
    if (searchKeyword.trim()) {
      const keyword = searchKeyword.toLowerCase().trim();
      suppliers = suppliers.filter((supplier) =>
        getMultilingualDisplayName(supplier.equipmentSupplierName)?.toLowerCase().includes(keyword)
      );
    }

    // 表示言語基準でソート
    return [...suppliers].sort((a, b) =>
      getMultilingualDisplayName(a.equipmentSupplierName).localeCompare(getMultilingualDisplayName(b.equipmentSupplierName))
    );
  }, [equipmentSuppliers, searchKeyword]);

  /**
   * 類似候補（入力中にリアルタイム表示）
   * 日本語名・英語名のいずれかが類似していれば表示する（EquipmentGroupSelectModalと同じ単一リスト方式）
   */
  const similarSuppliers = useMemo(() => {
    const keywordJa = newSupplierName.toLowerCase().trim();
    const keywordEn = newSupplierNameEn.toLowerCase().trim();
    if (keywordJa.length < 2 && keywordEn.length < 2) return [];
    return equipmentSuppliers
      .filter(sup => !sup.deleted)
      .map(sup => ({
        sup,
        matchJa: keywordJa.length >= 2 && !!sup.equipmentSupplierName?.ja?.toLowerCase().includes(keywordJa),
        matchEn: keywordEn.length >= 2 && !!sup.equipmentSupplierName?.en?.toLowerCase().includes(keywordEn),
      }))
      .filter(({ matchJa, matchEn }) => matchJa || matchEn)
      .slice(0, 5);
  }, [newSupplierName, newSupplierNameEn, equipmentSuppliers]);

  /** 検索キーワードに一致する部分をハイライト表示 */
  const highlightText = useCallback((text: string | undefined | null, keyword: string) => {
    if (!text || !keyword.trim()) {
      return <>{text}</>;
    }

    const lowerText = text.toLowerCase();
    const lowerKeyword = keyword.toLowerCase().trim();
    const index = lowerText.indexOf(lowerKeyword);

    if (index === -1) {
      return <>{text}</>;
    }

    const before = text.slice(0, index);
    const match = text.slice(index, index + keyword.trim().length);
    const after = text.slice(index + keyword.trim().length);

    return (
      <>
        {before}
        <Box as="span" bg="yellow.200" fontWeight="bold">
          {match}
        </Box>
        {after}
      </>
    );
  }, []);

  /** サプライヤー選択ハンドラー */
  const handleSelect = useCallback((supplier: EquipmentSupplierType) => {
    onSelect(supplier);
  }, [onSelect]);

  /** 検索キーワード変更ハンドラー */
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchKeyword(e.target.value);
  }, []);

  /** 自由入力モードを開く */
  const handleOpenAddMode = useCallback(() => {
    setIsAddMode(true);
    setNewSupplierName(searchKeyword.trim());
    setNewSupplierNameEn('');
    setAddErrors({});
  }, [searchKeyword]);

  /** 自由入力モードを閉じる */
  const handleCloseAddMode = useCallback(() => {
    setIsAddMode(false);
    setNewSupplierName('');
    setNewSupplierNameEn('');
    setAddErrors({});
  }, []);

  /** 仮登録実行 */
  const handleRegister = async () => {
    if (!onRegisterCandidate) return;

    const errors: { name?: string; nameEn?: string } = {};
    const trimmedName = newSupplierName.trim();
    if (!trimmedName) {
      errors.name = t('components.EquipmentSupplierSelectModal.supplierNameRequired');
    } else if (trimmedName.length > 100) {
      errors.name = t('components.EquipmentSupplierSelectModal.max100chars');
    }
    const trimmedNameEn = newSupplierNameEn.trim();
    if (!trimmedNameEn) {
      errors.nameEn = t('components.EquipmentSupplierSelectModal.supplierNameEnRequired');
    } else if (trimmedNameEn.length > 100) {
      errors.nameEn = t('components.EquipmentSupplierSelectModal.max100chars');
    }

    if (Object.keys(errors).length > 0) {
      setAddErrors(errors);
      return;
    }

    setIsRegistering(true);
    setAddErrors({});

    try {
      const result = await onRegisterCandidate(trimmedName, trimmedNameEn);
      if (result) {
        onSelect(result);
      }
    } finally {
      setIsRegistering(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent maxW="500px">
        <ModalHeader>{t('components.EquipmentSupplierSelectModal.title')}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {!isAddMode ? (
            <>
              {/* 検索ボックス */}
              <InputGroup mb={4}>
                <InputLeftElement pointerEvents="none">
                  <SearchIcon color="gray.400" />
                </InputLeftElement>
                <Input
                  placeholder={t('components.EquipmentSupplierSelectModal.search.placeholder')}
                  value={searchKeyword}
                  onChange={handleSearchChange}
                  autoFocus
                />
              </InputGroup>

              {/* 検索結果件数 */}
              <HStack mb={3} justifyContent="space-between">
                <Text fontSize="sm" color="gray.600">
                  {searchKeyword.trim() ? (
                    <>
                      {t('components.EquipmentSupplierSelectModal.search.result')}: <Badge colorScheme="blue">{filteredSuppliers.length}</Badge> {t('components.EquipmentSupplierSelectModal.search.count')}
                      {filteredSuppliers.length !== equipmentSuppliers.length && (
                        <Text as="span" ml={2}>
                          {t('components.EquipmentSupplierSelectModal.search.totalOf', { total: equipmentSuppliers.length })}
                        </Text>
                      )}
                    </>
                  ) : (
                    <>
                      {t('components.EquipmentSupplierSelectModal.search.totalLabel')} <Badge colorScheme="gray">{equipmentSuppliers.length}</Badge> {t('components.EquipmentSupplierSelectModal.search.count')}
                    </>
                  )}
                </Text>
              </HStack>

              {/* サプライヤーリスト */}
              <VStack
                align="stretch"
                spacing={2}
                overflowY="auto"
                maxH="400px"
                pr={2}
                onWheel={(event) => {
                  event.currentTarget.scrollTop += event.deltaY;
                }}
              >
                {filteredSuppliers.length === 0 ? (
                  <Box p={4} textAlign="center" color="gray.500">
                    <Text>{t('components.EquipmentSupplierSelectModal.search.noResults')}</Text>
                    {onRegisterCandidate && (
                      <Button
                        mt={3}
                        size="sm"
                        colorScheme="orange"
                        leftIcon={<AddIcon />}
                        onClick={handleOpenAddMode}
                      >
                        {t('components.EquipmentSupplierSelectModal.addNewSupplier')}
                      </Button>
                    )}
                  </Box>
                ) : (
                  filteredSuppliers.map((sup) => (
                    <Box
                      key={sup.equipmentSupplierId}
                      p={3}
                      border="1px solid"
                      borderColor={sup.isCandidate ? 'orange.200' : 'gray.200'}
                      borderRadius="md"
                      cursor="pointer"
                      bg={sup.isCandidate ? 'orange.50' : 'transparent'}
                      _hover={{ bg: sup.isCandidate ? 'orange.100' : 'blue.50', borderColor: sup.isCandidate ? 'orange.400' : 'blue.300' }}
                      onClick={() => handleSelect(sup)}
                      transition="all 0.2s"
                    >
                      <Text fontSize="md">
                        {highlightText(getMultilingualDisplayName(sup.equipmentSupplierName), searchKeyword)}
                        {sup.isCandidate && <Tag size="sm" colorScheme="orange" ml={2}>{t('components.EquipmentSupplierSelectModal.candidate')}</Tag>}
                      </Text>
                    </Box>
                  ))
                )}
              </VStack>

              {/* 自由入力ボタン（検索結果がある場合はフッター付近に表示） */}
              {onRegisterCandidate && filteredSuppliers.length > 0 && (
                <>
                  <Divider my={3} />
                  <Button
                    size="sm"
                    variant="outline"
                    colorScheme="orange"
                    leftIcon={<AddIcon />}
                    onClick={handleOpenAddMode}
                  >
                    {t('components.EquipmentSupplierSelectModal.addNotInList')}
                  </Button>
                </>
              )}
            </>
          ) : (
            /* 自由入力フォーム */
            <VStack align="stretch" spacing={4}>
              <Alert status="info" borderRadius="md">
                <AlertIcon />
                <Text fontSize="sm">
                  {t('components.EquipmentSupplierSelectModal.candidateInfo')}<br />{t('components.EquipmentSupplierSelectModal.candidateAdminConfirm')}
                </Text>
              </Alert>

              <FormControl isInvalid={!!addErrors.name}>
                <FormLabel fontWeight="bold">{t('components.EquipmentSupplierSelectModal.supplierName')}</FormLabel>
                <Input
                  value={newSupplierName}
                  onChange={(e) => setNewSupplierName(e.target.value)}
                  placeholder={t('components.EquipmentSupplierSelectModal.supplierNamePlaceholder')}
                  maxLength={InputLimits.EQUIPMENT_MASTER_NAME}
                  autoComplete="off"
                  autoFocus
                />
                <FormErrorMessage>{addErrors.name}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!addErrors.nameEn}>
                <FormLabel fontWeight="bold">{t('components.EquipmentSupplierSelectModal.supplierNameEn')}</FormLabel>
                <Input
                  value={newSupplierNameEn}
                  onChange={(e) => setNewSupplierNameEn(e.target.value)}
                  placeholder={t('components.EquipmentSupplierSelectModal.supplierNameEnPlaceholder')}
                  maxLength={InputLimits.EQUIPMENT_MASTER_NAME}
                  autoComplete="off"
                />
                <FormErrorMessage>{addErrors.nameEn}</FormErrorMessage>
              </FormControl>

              {/* 類似候補表示 */}
              {similarSuppliers.length > 0 && (
                <Box>
                  <Text fontSize="sm" fontWeight="bold" color="orange.600" mb={1}>
                    {t('components.EquipmentSupplierSelectModal.similarFound')}
                  </Text>
                  <VStack align="stretch" spacing={1}>
                    {similarSuppliers.map(({ sup, matchJa, matchEn }) => (
                      <Box
                        key={sup.equipmentSupplierId}
                        p={2}
                        bg="orange.50"
                        borderRadius="md"
                        border="1px solid"
                        borderColor="orange.200"
                        cursor="pointer"
                        _hover={{ bg: 'orange.100' }}
                        onClick={() => handleSelect(sup)}
                      >
                        <HStack justifyContent="space-between" align="start">
                          <VStack align="start" spacing={0}>
                            <Text fontSize="sm">
                              {matchJa ? highlightText(sup.equipmentSupplierName?.ja, newSupplierName) : sup.equipmentSupplierName?.ja}
                            </Text>
                            <Text fontSize="xs" color="gray.600">
                              {matchEn ? highlightText(sup.equipmentSupplierName?.en, newSupplierNameEn) : sup.equipmentSupplierName?.en}
                            </Text>
                          </VStack>
                          {sup.isCandidate && <Tag size="sm" colorScheme="orange">{t('components.EquipmentSupplierSelectModal.candidate')}</Tag>}
                        </HStack>
                      </Box>
                    ))}
                  </VStack>
                </Box>
              )}

              <HStack justifyContent="flex-end" spacing={2}>
                <Button onClick={handleCloseAddMode} isDisabled={isRegistering}>
                  {t('components.EquipmentSupplierSelectModal.back')}
                </Button>
                <Button
                  colorScheme="orange"
                  onClick={handleRegister}
                  isLoading={isRegistering}
                  isDisabled={isRegistering}
                >
                  {t('components.EquipmentSupplierSelectModal.registerAsCandidate')}
                </Button>
              </HStack>
            </VStack>
          )}
        </ModalBody>
        <ModalFooter>
          {!isAddMode && <Button onClick={onClose}>{t('components.EquipmentSupplierSelectModal.close')}</Button>}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default EquipmentSupplierSelectModal;
